<script setup lang="ts">
import { computed } from "vue";
import IconChevronLeft from "@/assets/icons/IconChevronLeft.vue";
import IconChevronRight from "@/assets/icons/IconChevronRight.vue";
import type {Paginator} from "@/types/paginator";

interface PaginatorProps {
  paginator: Paginator;
}

interface PaginatorEmits {
  (_e: "goToPage", _page: number): void;
}

const props = defineProps<PaginatorProps>();
const emit = defineEmits<PaginatorEmits>();

// Текущая страница из пагинатора
const currentPage = computed(() => props.paginator.current_page ?? 1);
// Общее количество страниц
const totalPages = computed(() => props.paginator.total_pages);

// Вычисляемое свойство для генерации массива отображаемых страниц
const visiblePages = computed<(number | string)[]>(() => {
  const pages: (number | string)[] = [];
  const current: number = currentPage.value;
  const total: number = totalPages.value;

  // Если страниц мало (до 9), показываем все
  if (total <= 9) {
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
    return pages;
  }

  // Всегда показываем первые 3 страницы
  pages.push(1, 2, 3);

  // Определяем, нужно ли показывать среднюю часть с активной страницей
  if (current > 5 && current < total - 4) {
    // Активная страница в середине
    // Показываем: 1 2 3 ... prev current next ... 18 19 20
    pages.push("...");
    pages.push(current - 1, current, current + 1);
    pages.push("...");
  } else if (current === 5) {
    // Активная страница 5 - нужно показать страницу 4 и 6
    // Показываем: 1 2 3 4 5 6 ... 18 19 20
    pages.push(4, 5, 6);
    pages.push("...");
  } else if (current >= 3 && current < 5) {
    // Активная страница между началом и серединой (3-4)
    // Показываем страницы от 4 до 5, чтобы показать соседей
    for (let i = 4; i <= 5; i++) {
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }
    pages.push("...");
  } else if (current >= total - 4) {
    // Активная страница ближе к концу
    // Показываем: 1 2 3 ... последние 6 страниц
    pages.push("...");
  } else {
    // Активная страница 1-2 (уже в первых трёх)
    pages.push("...");
  }

  // Всегда показываем последние 3 страницы
  // Если активная близка к концу, показываем больше
  if (current >= total - 4) {
    for (let i = total - 5; i <= total; i++) {
      if (!pages.includes(i) && i > 3) {
        pages.push(i);
      }
    }
  } else {
    pages.push(total - 2, total - 1, total);
  }

  return pages;
});

const goToPreviousPage = () => {
  if (currentPage.value > 1) {
    emit("goToPage", currentPage.value - 1);
  }
};

const goToNextPage = () => {
  if (currentPage.value < totalPages.value) {
    emit("goToPage", currentPage.value + 1);
  }
};

const goToPage = (page: number | string) => {
  if (typeof page === "number") {
    emit("goToPage", page);
  }
};
</script>

<template>
  <div class="paginator">
    <button
      :disabled="!paginator.previous_page"
      class="paginator__button"
      @click="goToPreviousPage"
    >
      <IconChevronLeft />
    </button>
    <div
      v-for="(item, index) in visiblePages"
      :key="index"
      class="paginator__item"
      :class="{
        'paginator__item--active': currentPage === item,
        'paginator__item--dots': item === '...'
      }"
      @click="goToPage(item)"
    >
      {{ item }}
    </div>
    <button
      class="paginator__button"
      :disabled="!paginator.next_page"
      @click="goToNextPage"
    >
      <IconChevronRight />
    </button>
  </div>
</template>

<style scoped lang="scss">
.paginator {
  display: flex;
  align-items: center;
  gap: 16px;

  &__item {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border-radius: 40px;
    padding:  0 8px;
    width: 32px;
    height: 32px;
    font-size: 14px;
    font-weight: 500;
    color: var(--text-text-main);

    &--active {
      background-color: var(--background-bg-white);
    }

    &--dots {
      cursor: default;
      pointer-events: none;
    }
  }

  &__button {
    appearance: none;
    -webkit-appearance: none;
    cursor: pointer;
    background-color: transparent;
    color: var(--icon-icon-primary-black);

    &:disabled {
      color: var(--icon-icon-disabled);
    }
  }
}
</style>